import { DragOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";

import { useDragHandler } from "hooks/useDragHandler";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { Theme, useTheme } from "styles/theme";

import styles from "./DebugThemeToggle.module.scss";

import type { Position } from "hooks/useDragHandler";

const MARGIN = 15;
const TOGGLE_SIZE = 60;
const POS_X_KEY = "theme_toggle_pos_x";
const POS_Y_KEY = "theme_toggle_pos_y";

/** Get the desired initial position for DebugThemeToggle. */
const getInitialPosition = (windowHeight: number, windowWidth: number): Position => {
  const toggleOffset = TOGGLE_SIZE + MARGIN;
  const maxX = windowWidth - toggleOffset;
  const maxY = windowHeight - toggleOffset;
  const savedX = localStorage.getItem(POS_X_KEY);
  const savedY = localStorage.getItem(POS_Y_KEY);
  return [
    Math.min(savedX != null ? parseInt(savedX, 10) : maxX, maxX),
    Math.min(savedY != null ? parseInt(savedY, 10) : maxY, maxY),
  ];
};

export default function DebugThemeToggle() {
  const [initialPosition, setInitialPosition] = useState<Position>(() =>
    getInitialPosition(window.innerHeight, window.innerWidth)
  );

  // Add listener to keep widget within viewport bounds on resize.
  useEffect(() => {
    let debounce: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        const initialPosition = getInitialPosition(window.innerHeight, window.innerWidth);
        localStorage.setItem(POS_X_KEY, initialPosition[0].toString());
        localStorage.setItem(POS_Y_KEY, initialPosition[1].toString());
        setInitialPosition(initialPosition);
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(debounce);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const { theme, setTheme } = useTheme();
  const visible = useFeatureFlag("internalThemeToggle");
  const { dragging, contentRef, handleRef } = useDragHandler<HTMLButtonElement>({
    margin: 15,
    initialPosition,
    onDragEnd: (x, y) => {
      localStorage.setItem(POS_X_KEY, x.toString());
      localStorage.setItem(POS_Y_KEY, y.toString());
    },
  });

  if (!visible) {
    return null;
  }

  return (
    <button
      type="button"
      ref={contentRef}
      data-theme={theme.toLowerCase()}
      data-dragging={dragging}
      className={styles.toggle}
      onClick={() => setTheme(theme === Theme.LIGHT ? Theme.DARK : Theme.LIGHT)}
    >
      <div className={styles.handle} ref={handleRef}>
        <DragOutlined className="flex content-center items-center" />
      </div>
      <div className={styles.front}>&#127774;</div>
      <div className={styles.back}>&#127770;</div>
    </button>
  );
}
